import type { LogLevel, LogRecord, LogSink, Logger, LoggerConfig } from './types';
import { LOG_LEVEL_ORDER } from './types';
import { consoleSink, noopSink } from './sinks';
import { defaultScrub } from './scrubber';

class LoggerImpl implements Logger {
  private readonly config: Required<Omit<LoggerConfig, 'scrub'>> & {
    scrub: (ctx: Record<string, unknown>) => Record<string, unknown>;
  };
  private readonly boundContext: Record<string, unknown>;

  constructor(
    config: LoggerConfig,
    boundContext: Record<string, unknown> = {},
  ) {
    this.config = {
      level: config.level,
      sinks: config.sinks,
      scrub: config.scrub ?? defaultScrub,
      now: config.now ?? (() => new Date()),
    };
    this.boundContext = boundContext;
  }

  debug(message: string, context?: Record<string, unknown>): void {
    this.emit('debug', message, undefined, context);
  }

  info(message: string, context?: Record<string, unknown>): void {
    this.emit('info', message, undefined, context);
  }

  warn(message: string, context?: Record<string, unknown>): void {
    this.emit('warn', message, undefined, context);
  }

  error(message: string, error?: unknown, context?: Record<string, unknown>): void {
    this.emit('error', message, error, context);
  }

  child(context: Record<string, unknown>): Logger {
    return new LoggerImpl(this.config, { ...this.boundContext, ...context });
  }

  private emit(
    level: LogLevel,
    message: string,
    error: unknown,
    context: Record<string, unknown> | undefined,
  ): void {
    if (LOG_LEVEL_ORDER[level] < LOG_LEVEL_ORDER[this.config.level]) return;

    const merged = { ...this.boundContext, ...(context ?? {}) };
    const scrubbed =
      Object.keys(merged).length > 0 ? this.config.scrub(merged) : undefined;

    const record: LogRecord = {
      level,
      message,
      timestamp: this.config.now().toISOString(),
      ...(scrubbed !== undefined ? { context: scrubbed } : {}),
      ...(error !== undefined ? { error } : {}),
    };

    for (const sink of this.config.sinks) {
      try {
        sink.write(record);
      } catch {
        // A failing sink must never take down the call site. Silently
        // drop — the console sink is the diagnostic backstop and is
        // designed not to throw.
      }
    }
  }
}

export function createLogger(config: LoggerConfig): Logger {
  return new LoggerImpl(config);
}

/**
 * Lazy global logger. Apps call `configureLogger({...})` once at
 * startup; packages import `logger` from `@repo/logger` and trust that
 * config happened upstream. If no app has configured, we fall back to
 * the console sink in dev (for visibility) or the noop sink in prod.
 */
let globalLogger: Logger | null = null;

export function configureLogger(config: LoggerConfig): Logger {
  globalLogger = createLogger(config);
  return globalLogger;
}

export function getLogger(): Logger {
  if (globalLogger) return globalLogger;
  globalLogger = createLogger(defaultConfig());
  return globalLogger;
}

function defaultConfig(): LoggerConfig {
  const isProd = detectProd();
  return {
    level: isProd ? 'warn' : 'debug',
    sinks: isProd ? [noopSink] : [consoleSink],
  };
}

function detectProd(): boolean {
  try {
    // Works in Node, Next.js server, Metro, web bundlers — each inlines
    // `process.env.NODE_ENV` as a string literal.
    const env = (globalThis as { process?: { env?: { NODE_ENV?: string } } }).process?.env?.NODE_ENV;
    return env === 'production';
  } catch {
    return false;
  }
}

/**
 * Proxy that defers every method to the currently-configured logger.
 * This is what library code imports, so that a late `configureLogger`
 * call still affects already-imported modules.
 */
export const logger: Logger = {
  debug: (msg, ctx) => getLogger().debug(msg, ctx),
  info: (msg, ctx) => getLogger().info(msg, ctx),
  warn: (msg, ctx) => getLogger().warn(msg, ctx),
  error: (msg, err, ctx) => getLogger().error(msg, err, ctx),
  child: (ctx) => getLogger().child(ctx),
};
